// web/src/lib/money-format.ts

/**
 * Rendering money for humans. Storage and arithmetic are untouched by this
 * file: every amount in the system is an integer number of micro-USD.
 *
 * WHY THE DECIMAL COUNT ADAPTS. A fixed 2 decimals is right for a balance and
 * catastrophic for a usage row. At the magnitudes this system actually bills
 * at, `toFixed(2)` does not round an amount — it deletes it. A measured
 * example: a 10.08-second Soniox stream with translation cost $0.000776 from
 * the provider, charged at `cost × K` (K = 2.0) = 1552 µUSD. Two decimals
 * render that as "$0.00", so the ledger's whole Amount column read as zeros
 * for usage rows; only top-ups and hour-long sessions showed anything.
 *
 * Choosing the precision by magnitude gives every amount meaningful
 * significant digits:
 *
 *   |x| >= $1      2 decimals   "$12.50"
 *   |x| >= $0.01   4 decimals   "$0.0265"
 *   otherwise      6 decimals   "$0.001552"   (the full µUSD atom, lossless)
 *
 * Balances and top-ups are dollar-magnitude and so keep the familiar two
 * decimals; usage rows finally carry content.
 *
 * MIRRORED IN `src/services/money-format.ts`. The dashboard bundle and the
 * Worker are separate TypeScript projects (the root tsconfig excludes `web/`,
 * and `web/tsconfig.json` only includes `web/src`) built by different
 * toolchains, so neither can import the other. The rule therefore exists
 * twice, and a test on each side restates the same case table so a change to
 * one copy fails the other — the pattern already used for
 * `sonioxStartFloorMicroUsd` and its sokuji-react mirror.
 *
 * The one intentional difference from the worker copy is the sign/currency
 * convention: this side owns the `$` and emits "-$2.50", while the worker
 * emits a bare "-2.50" that its callers wrap.
 */

/**
 * Conversion anchor: 1 USD = 1,000,000 micro-USD. Same name as the worker-side
 * constant in src/services/pricing.ts; this is a separate browser bundle and
 * cannot import worker code, so the value is duplicated but the name is not.
 */
export const MICRO_USD_PER_USD = 1_000_000;

/** A formatted magnitude carrying no value at all: "0", "0.00", "0.000000". */
const ALL_ZEROS = /^0(?:\.0+)?$/;

/**
 * Decimal places for a NON-NEGATIVE USD magnitude, chosen by its size.
 *
 * Exact zero is carved OUT of the table, which never really applied to it:
 * zero has no magnitude to select a bucket by. Every ledger UI shows an empty
 * balance as "$0.00", and "$0.000000" reads as a precision artifact rather
 * than as "nothing".
 *
 * That leaves 0 rendering "0.00" while 1 µUSD renders "0.000001", which looks
 * asymmetric but is exactly the point: one means no money moved, the other
 * means a very small amount did, and those two must not look alike.
 */
function formatUsdMagnitude(absUsd: number): string {
  if (absUsd === 0) return '0.00';
  if (absUsd >= 1) return absUsd.toFixed(2);
  if (absUsd >= 0.01) return absUsd.toFixed(4);
  return absUsd.toFixed(6);
}

/** Rendered in place of an amount we do not have. Not "$0.00": that would
 *  assert no money moved, when the truth is that the value is unusable. */
const UNKNOWN_AMOUNT = '—';

/**
 * Render a raw micro-USD amount as a signed dollar string with
 * magnitude-adaptive precision. Never render the raw integer.
 *
 * The sign is applied to the FORMATTED magnitude, not the raw number, so an
 * amount too small to survive its own precision bucket cannot render as
 * "-$0.000000" — which reads as a rendering bug rather than as a balance a
 * fraction of a micro-USD overdrawn. A representable negative stays signed.
 */
export function formatSignedUsd(microUsd: number): string {
  if (typeof microUsd !== 'number' || !Number.isFinite(microUsd)) return UNKNOWN_AMOUNT;
  const usd = microUsd / MICRO_USD_PER_USD;
  const magnitude = formatUsdMagnitude(Math.abs(usd));
  const sign = usd < 0 && !ALL_ZEROS.test(magnitude) ? '-' : '';
  return `${sign}$${magnitude}`;
}
